import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LogOut, LayoutGrid, Users, Settings, Menu, X, UserCircle } from "lucide-react";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";
import { useAuth } from "../lib/auth";

const NAV = [
  { to: "/admin", label: "Projects", icon: LayoutGrid, end: true },
  { to: "/admin/clients", label: "Clients", icon: Users },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

/**
 * AdminLayout — fixed sidebar at lg+, hamburger drawer below lg.
 * Mirrors the proven mobile pattern used in ClientLayout.jsx, reusing
 * the same NAV array.
 */
export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Close drawer on Esc
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const sidebarBody = (
    <>
      <div className="px-6 py-7 border-b border-[color:var(--atech-border)] flex items-center justify-between">
        <Logo />
        <button
          className="lg:hidden text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] -mr-1 p-1"
          onClick={() => setMobileOpen(false)}
          data-testid="admin-mobile-drawer-close"
          aria-label="Close menu"
        >
          <X size={18} />
        </button>
      </div>
      <nav className="flex-1 px-4 py-6 space-y-1">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              [
                "flex items-center gap-3 px-3 py-2.5 text-sm rounded-[2px] transition-colors",
                isActive
                  ? "bg-[color:var(--atech-surface-hover)] text-[color:var(--atech-text)] border-l-2 border-[color:var(--atech-copper)] pl-[10px]"
                  : "text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:bg-[color:var(--atech-surface-hover)]/60",
              ].join(" ")
            }
            data-testid={`nav-${label.toLowerCase()}`}
          >
            <Icon size={16} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="px-6 py-5 border-t border-[color:var(--atech-border)]">
        <NavLink
          to="/admin/settings?tab=account"
          onClick={() => setMobileOpen(false)}
          className="flex items-center gap-2.5 mb-3 group"
          data-testid="nav-account"
        >
          <UserCircle size={26} className="text-[color:var(--atech-text-dim)] group-hover:text-[color:var(--atech-copper)] transition-colors" />
          <div className="min-w-0 flex-1">
            <div className="text-xs text-[color:var(--atech-text)] truncate group-hover:text-[color:var(--atech-copper)] transition-colors" data-testid="admin-name">
              {user?.name || user?.email}
            </div>
            <div className="label mt-0.5 text-[color:var(--atech-text-muted)]">Account · Practice admin</div>
          </div>
        </NavLink>
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={onLogout}
            className="text-xs flex items-center gap-2 text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] transition-colors"
            data-testid="logout-button"
          >
            <LogOut size={14} /> Sign out
          </button>
          <ThemeToggle testId="admin-theme-toggle" />
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[color:var(--atech-bg)] text-[color:var(--atech-text)]">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="admin-mobile-backdrop"
        />
      )}

      {/* Sidebar — fixed on desktop, slide-in drawer on mobile */}
      <aside
        className={[
          "fixed inset-y-0 left-0 z-50 w-64 border-r border-[color:var(--atech-border)] bg-[color:var(--atech-surface)] flex flex-col",
          "transform transition-transform duration-200",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        ].join(" ")}
        data-testid="admin-mobile-drawer"
      >
        {sidebarBody}
      </aside>

      {/* Mobile top bar (only below lg) */}
      <header
        className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 border-b border-[color:var(--atech-border)] bg-[color:var(--atech-surface)]/85 backdrop-blur"
        data-testid="admin-mobile-topbar"
      >
        <button
          onClick={() => setMobileOpen(true)}
          className="text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] p-1 -ml-1"
          data-testid="admin-mobile-menu-toggle"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
        <Logo compact />
        <div className="w-6" />
      </header>

      <main className="lg:ml-64 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}

export default AdminLayout;
